import { type FC, useEffect } from "react";

import { RefreshIcon, SearchIcon } from "@moment/icons";

import { Button } from "../Button";
import { InputText } from "../InputText";
import { BasicTooltip } from "../Tooltip";
import { useDebouncedOnChangeSearchQuery } from "./useDebouncedOnChangeSearchQuery";
import { useSearchTextInputControls } from "./useSearchTextInputControls";

export type InputSearchToggleProps = {
	label: string;
	defaultSearchQueryValue?: string;
	onSearchQueryChange: (query: string) => void;
	disabled?: boolean;
};

export const InputSearchToggle: FC<InputSearchToggleProps> = ({
	label,
	defaultSearchQueryValue,
	onSearchQueryChange,
	disabled,
}) => {
	const { showSearchTextInput, setShowSearchTextInput, searchRef } =
		useSearchTextInputControls(defaultSearchQueryValue);

	const { currSearchQuery, debouncing, debouncedOnChangeSearchQuery } =
		useDebouncedOnChangeSearchQuery(defaultSearchQueryValue, onSearchQueryChange);

	useEffect(() => {
		// IMPORTANT: Ensure that the reset button actually resets the search query, but *DOESN’T*
		// fire spuriously. Specifically, without this conditional, we will fire twice for each
		// debounced change.
		if (defaultSearchQueryValue === currSearchQuery) {
			return;
		}

		debouncedOnChangeSearchQuery(defaultSearchQueryValue || "");
	}, [defaultSearchQueryValue]);

	return showSearchTextInput || currSearchQuery ? (
		<InputText
			ref={searchRef}
			className="w-52"
			size="sm"
			disableElevation
			StartIcon={
				debouncing ? (
					<RefreshIcon size={16} className="animate-spin" aria-hidden="true" />
				) : (
					<SearchIcon size={16} aria-hidden="true" />
				)
			}
			placeholder={`${label}...`}
			value={currSearchQuery}
			onKeyDown={(e) => {
				if (e.key === "Escape") {
					searchRef.current?.blur();
				}
			}}
			onChange={(e) => debouncedOnChangeSearchQuery(e.target.value)}
			hideClearValueIfEmpty
			onBlur={() => setShowSearchTextInput(false)}
			clearValue
		/>
	) : (
		<BasicTooltip content={label}>
			<BasicTooltip
				content={disabled && "Table is not editable"}
				triggerProps={{ asChild: true }}
			>
				<Button
					aria-label={label}
					level="tertiary"
					onClick={() => setShowSearchTextInput(true)}
					icon={SearchIcon}
					disabled={disabled}
				/>
			</BasicTooltip>
		</BasicTooltip>
	);
};
